/**
 * Accordion Module
 *
 * Accessible accordion/collapsible panels with smooth height animations.
 * Syncs ARIA states with visual state via class changes and MutationObserver.
 *
 * Features:
 * - ARIA-compliant (aria-expanded, aria-controls, inert)
 * - Optional button text state swap (open/closed text)
 * - Smooth GSAP height animations
 * - Focus management (returns focus to button when closing)
 * - Default open state support
 *
 * Structure:
 *   <div data-hs-accordion="wrapper" data-hs-accordion-default="open">
 *     <button data-hs-accordion="toggle">
 *       Question
 *       <span data-hs-accordion="state">Open</span>
 *     </button>
 *     <div data-hs-accordion="content">
 *       <div data-hs-height="element">Answer content</div>
 *     </div>
 *   </div>
 */

import {
  querySelectorAll,
  querySelector,
  getSelector,
  globalConfig,
  animateHeight,
  setHeight,
} from '@utils';

interface EventHandler {
  element: Element | Document;
  event: string;
  handler: EventListener;
  options?: AddEventListenerOptions;
}

export function init(config: any) {
  const cleanup = {
    observers: [] as MutationObserver[],
    handlers: [] as EventHandler[],
  };

  const addObserver = (observer: MutationObserver): number => cleanup.observers.push(observer);
  const addHandler = (
    element: Element | Document,
    event: string,
    handler: EventListener,
    options?: AddEventListenerOptions
  ): void => {
    element.addEventListener(event, handler, options);
    cleanup.handlers.push({ element, event, handler, options });
  };

  function setupAccordionAccessibility(
    addObserver: (observer: MutationObserver) => number,
    addHandler: (
      element: Element | Document,
      event: string,
      handler: EventListener,
      options?: AddEventListenerOptions
    ) => void
  ): number {
    const accordionWrappers = querySelectorAll(config, 'wrapper');

    if (accordionWrappers.length === 0) {
      return 0;
    }

    let initializedCount = 0;

    accordionWrappers.forEach((wrapper, index) => {
      const button = querySelector(config, 'toggle', wrapper);
      const content = querySelector(config, 'content', wrapper);

      if (!button || !content) {
        return;
      }

      initializedCount++;

      // Check for visual height wrapper (contains data-hs-height="element")
      // If found, animate that instead of the content (content is for ARIA only)
      const heightSelector = `[${globalConfig.attributes.height}="${globalConfig.attributes.heightValue}"]`;
      const heightWrapper = wrapper.querySelector(heightSelector);
      const animationTarget = heightWrapper ? heightWrapper.parentElement : content;

      // Generate unique IDs
      const buttonId = `hs-accordion-btn-${index}`;
      const contentId = `hs-accordion-content-${index}`;

      // Get text state attributes (optional)
      const openText = wrapper.getAttribute(config.attributes.properties.open);
      const closedText = wrapper.getAttribute(config.attributes.properties.closed);
      const hasTextSwap = openText && closedText;

      // Find all state text elements (if text swap enabled)
      let stateElements = [];
      if (hasTextSwap) {
        const stateSelector = getSelector(config, 'state');
        stateElements = Array.from(button.querySelectorAll(stateSelector));
      }

      // Function to update state text elements
      function updateStateText(text: string): void {
        if (!hasTextSwap) return;
        stateElements.forEach((el) => {
          el.textContent = text;
        });
      }

      // Set initial IDs and ARIA attributes
      button.setAttribute('id', buttonId);
      content.setAttribute('id', contentId);
      content.setAttribute('role', 'region');
      content.setAttribute('aria-labelledby', buttonId);
      button.setAttribute('aria-controls', contentId);

      // Function to check if accordion is open
      function isAccordionOpen() {
        return wrapper.classList.contains(globalConfig.classes.active);
      }

      // Update ARIA states based on current visual state
      function updateARIAStates() {
        const isOpen = isAccordionOpen();
        const wasOpen = button.getAttribute('aria-expanded') === 'true';

        // If closing and focus is inside content, return focus first
        if (wasOpen && !isOpen && content.contains(document.activeElement)) {
          const buttonEl = button as HTMLElement;
          buttonEl.focus();
        }

        // Update ARIA attributes
        button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        const contentEl = content as HTMLElement;
        contentEl.inert = !isOpen; // Use inert instead of aria-hidden

        // Update state text if text swap enabled
        if (hasTextSwap && stateElements.length > 0) {
          updateStateText(isOpen ? openText : closedText);
        }

        // Animate height wrapper (visual container, not ARIA content)
        animateHeight(animationTarget, isOpen, { duration: 300, ease: 'power2.inOut' });
      }

      // Handle default open state
      const defaultState = wrapper.getAttribute(config.attributes.properties.default);
      if (defaultState && defaultState.toLowerCase() === 'open') {
        wrapper.classList.add(globalConfig.classes.active);
      }

      // Set initial height without animation
      setHeight(animationTarget, isAccordionOpen());

      // Set initial state based on existing is-active class
      updateARIAStates();

      // Add click handler to button
      const clickHandler = (e: Event) => {
        e.preventDefault();
        wrapper.classList.toggle(globalConfig.classes.active);
      };
      addHandler(button, 'click', clickHandler);

      // Monitor for class changes on wrapper
      const observer = new MutationObserver(() => {
        updateARIAStates();
      });

      observer.observe(wrapper, {
        attributes: true,
        attributeFilter: ['class'],
      });

      addObserver(observer);
    });

    return initializedCount;
  }

  setupAccordionAccessibility(addObserver, addHandler);

  return {
    result: 'accordion initialized',
    destroy: () => {
      // Disconnect all observers
      cleanup.observers.forEach((obs) => obs.disconnect());
      cleanup.observers.length = 0;

      // Remove all event listeners
      cleanup.handlers.forEach(({ element, event, handler, options }) => {
        element.removeEventListener(event, handler, options);
      });
      cleanup.handlers.length = 0;
    },
  };
}
